import useSocketReconnect from '../../utils/useSocketReconnect';
import { useEffect, useRef, useState } from "react";
import { fetchAPI } from "../../utils/utils";
import { getPersistentItem, setPersistentItem } from "../../utils/storage";
import UserInfo from "../login/UserInfo";
import Pressable from "../other/Pressable";
import HeaderTabs from "../interface/HeaderTabs";
import { can } from "../../utils/permissions";
import StatsGlobalAttendanceTab from "./StatsGlobalAttendanceTab";
import StatsIndividualTab from "./StatsIndividualTab";
import StatsExportTab from "./StatsExportTab";
import StatsTecnicaTab from "./StatsTecnicaTab";
import StatsAssaigsTab from "./StatsAssaigsTab";
import StatsProvesTab from "./StatsProvesTab";
import StatsPositionsTab from "./StatsPositionsTab";
import StatsMapaTab from "./StatsMapaTab";
import SensitiveDataGuard from "../other/SensitiveDataGuard";
import './StatsApp.css';
import { getCastellerNameWithMote } from "../../utils/castellerName";
import { DEFAULT_STATS_EVENT_CATEGORIES, getHiddenRehearsalCategories } from './statsEventCategories';
import { createAssistenciesRequests, usersKey } from './assistenciesRequests';

function StatsApp(props) {
    useSocketReconnect(props.socket, () => {
        props.socket.emit('.request_all_events');
    });

    const { socket, userInfo, castellersInfo, setCastellersInfo } = props;

    const [chosenUsers, setChosenUsers] = useState([])
    const [events, setEvents] = useState([])
    const [eventCategories, setEventCategories] = useState(DEFAULT_STATS_EVENT_CATEGORIES)
    const [assistenciesDict, setAssistenciesDict] = useState({})
    const [passatFirst, setPassatFirst] = useState(true)

    // Load filtersEvents from localStorage or use defaults
    const [filtersEvents, setFiltersEvents] = useState(() => {
        const saved = getPersistentItem('stats_filtersEvents');
        if (saved) {
            try {
                const stored = JSON.parse(saved);
                return { ...stored, hiddenRehearsalCategories: getHiddenRehearsalCategories(stored) };
            } catch (e) {
                console.error('Error parsing stats_filtersEvents from localStorage:', e);
            }
        }
        return {
            activitats: true,
            assaigs: false,
            actuacions: false,
            hiddenRehearsalCategories: ['musics'],
            comercials: true,
            ids: [],
            canalla: true,
            music: true,
            titles: [],
        };
    })

    const [grups, setGrups] = useState({})

    const tecnicaRole = userInfo?.es_tecnica

    // Load minAttendees from localStorage or use default
    const [minAttendees, setMinAttendees] = useState(() => {
        const saved = getPersistentItem('stats_minAttendees');
        return saved ? parseInt(saved) || 0 : 0;
    })
    const [excludedEventIds, setExcludedEventIds] = useState([])
    const [activeTab, setActiveTab] = useState('assistencia-global')

    useEffect(() => {
        document.title = `Estadístiques - Aleta`;
    }, []);

    // Save filtersEvents to localStorage when it changes
    useEffect(() => {
        setPersistentItem('stats_filtersEvents', JSON.stringify(filtersEvents));
    }, [filtersEvents]);

    // Save minAttendees to localStorage when it changes
    useEffect(() => {
        setPersistentItem('stats_minAttendees', String(minAttendees));
    }, [minAttendees]);

    // Parse the current path to determine the active tab
    const getTabFromPath = () => {
        const path = window.location.pathname;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];

        // Support 'blame' as alias for 'tecnica' for backwards compatibility
        if (lastPart === 'blame') {
            return 'tecnica';
        }

        // Support 'assistencia' as alias for 'assistencia-global' for backwards compatibility
        if (lastPart === 'assistencia') {
            return 'assistencia-global';
        }

        if (['assistencia-global', 'individuals', 'posicions', 'assaigs', 'proves', 'tecnica', 'exportacio', 'mapa'].includes(lastPart)) {
            return lastPart;
        }
        
        return 'assistencia-global'; // Default tab
    };

    useEffect(() => {
        // Set initial tab based on URL path
        const tabFromPath = getTabFromPath();
        setActiveTab(tabFromPath);

        // Handle browser back/forward navigation
        const handlePopState = () => {
            const tabFromPath = getTabFromPath();
            setActiveTab(tabFromPath);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [])

    // useEffect(() => {
    //     // Tothom as default
    //     setChosenUsers(Object.keys(castellersInfo))
    // }, [
    //     Object.keys(castellersInfo).length,
    // ])

    useEffect(() => {
        let active = true;
        const updateCategories = data => {
            if (active && Array.isArray(data?.event_categories)) {
                setEventCategories(data.event_categories);
            }
        };
        fetchAPI('/calendar/categories', updateCategories, false, () => {});
        socket.on('.event_categories_changed', updateCategories);
        return () => {
            active = false;
            socket.off('.event_categories_changed', updateCategories);
        };
    }, [socket]);

    useEffect(() => {
        socket.emit('.request_all_events')

        const onEvents = data => setEvents(data)
        socket.on('.events', onEvents)

        return () => {
            socket.off('.events', onEvents)
        }
    }, [])

    useEffect(() => {
        fetchAPI('/notification_groups', fetchedGrups => {
            const infoFromIds = ids => Object.values(castellersInfo)
                .filter(info => ids.includes(info.id))
                .map(info => ({
                    value: info.id,
                    label: displayName(info.id),
                    color: info.has_notifications ? 'black' : 'red'
                }));

            setGrups(
                Object.fromEntries(
                    fetchedGrups
                        .map(group => [group.name, infoFromIds(group.ids)])
                )
            );
        });
    }, [
        Object.keys(castellersInfo).length,
    ]);


    useEffect(() => {
        if (filtersEvents.canalla) {
            // Remove canalla users from chosenUsers when filter is enabled
            setChosenUsers(prevUsers => prevUsers.filter(user => castellersInfo[user]?.canalla !== 1));
        }
    }, [filtersEvents.canalla]);

    useEffect(() => {
        if (filtersEvents.music) {
            // Remove music users from chosenUsers when filter is enabled
            setChosenUsers(prevUsers => prevUsers.filter(user => castellersInfo[user]?.music !== 1));
        }
    }, [filtersEvents.music]);

    // Only the chosen people not asked about yet: another order or fewer people asks nothing
    const assistenciesRequests = useRef(null);
    if (!assistenciesRequests.current) assistenciesRequests.current = createAssistenciesRequests();

    useEffect(() => {
        if (chosenUsers.length === 0) return;

        const users = assistenciesRequests.current.take(chosenUsers, events.length);
        if (users.length === 0) return;

        fetchAPI(`/assistencies_multiple?users=${users.join(',')}`, data => {
            setAssistenciesDict(prevAssistenciesDict => {
                const newAssistenciesDict = { ...prevAssistenciesDict };

                users
                    .filter(user => !(user in newAssistenciesDict))
                    .forEach(user => {
                        newAssistenciesDict[user] = {};
                        events.forEach(event => newAssistenciesDict[user][event.id] = 'No confirmat');
                    });

                data.forEach(res => {
                    if (!(res.user in newAssistenciesDict)) {
                        newAssistenciesDict[res.user] = {};
                    }
                    newAssistenciesDict[res.user][res.event] = res.assistencia;
                });

                return newAssistenciesDict;
            });
        }, true, () => assistenciesRequests.current.forget(users));
    }, [
        usersKey(chosenUsers),
        events.length
    ])

    const displayName = user => getCastellerNameWithMote(castellersInfo[user])

    // Helper function to change tab and update URL path
    const changeTab = (tab) => {
        setActiveTab(tab);
        
        // Get the base URL without the tab part
        const currentPath = window.location.pathname;
        const basePath = currentPath.endsWith('/assistencia-global') || currentPath.endsWith('/individuals') || currentPath.endsWith('/posicions') || currentPath.endsWith('/assaigs') || currentPath.endsWith('/proves') || currentPath.endsWith('/tecnica') || currentPath.endsWith('/exportacio') || currentPath.endsWith('/blame') || currentPath.endsWith('/assistencia') || currentPath.endsWith('/mapa')
            ? currentPath.substring(0, currentPath.lastIndexOf('/'))
            : currentPath;
        
        // Ensure we have a trailing slash on the basePath
        const normalizedBasePath = basePath.endsWith('/') ? basePath : basePath + '/';
        
        // Update URL without page reload
        const newPath = `${normalizedBasePath}${tab}`;
        window.history.pushState({ tab }, '', newPath);
    };

    const renderTabContent = () => {
        if (activeTab === 'assistencia-global') {
            return (
                <StatsGlobalAttendanceTab
                    filtersEvents={filtersEvents}
                    setFiltersEvents={setFiltersEvents}
                    passatFirst={passatFirst}
                    setPassatFirst={setPassatFirst}
                    minAttendees={minAttendees}
                    setMinAttendees={setMinAttendees}
                    events={events}
                    eventCategories={eventCategories}
                    castellersInfo={castellersInfo}
                    setChosenUsers={setChosenUsers}
                />
            );
        }

        if (activeTab === 'individuals') {
            return (
                <StatsIndividualTab
                    chosenUsers={chosenUsers}
                    setChosenUsers={setChosenUsers}
                    events={events}
                    eventCategories={eventCategories}
                    assistenciesDict={assistenciesDict}
                    castellersInfo={castellersInfo}
                    passatFirst={passatFirst}
                    setPassatFirst={setPassatFirst}
                    grups={grups}
                    displayName={displayName}
                    filtersEvents={filtersEvents}
                    setFiltersEvents={setFiltersEvents}
                    minAttendees={minAttendees}
                    setMinAttendees={setMinAttendees}
                />
            );
        }

        if (activeTab === 'exportacio') {
            return (
                <StatsExportTab
                    events={events}
                    excludedEventIds={excludedEventIds}
                    setExcludedEventIds={setExcludedEventIds}
                    castellersInfo={castellersInfo}
                    filtersEvents={filtersEvents}
                    setFiltersEvents={setFiltersEvents}
                    assistenciesDict={assistenciesDict}
                    chosenUsers={chosenUsers}
                />
            );
        }

        if (activeTab === 'posicions') {
            if (!can(userInfo, 'VIEW_POSITIONS_STATS')) return null;
            return (
                <SensitiveDataGuard userId={props.userId} userName={displayName(props.userId)} storageKey="stats_posicions">
                    <StatsPositionsTab
                        events={events}
                        castellersInfo={castellersInfo}
                        socket={socket}
                    />
                </SensitiveDataGuard>
            );
        }

        if (activeTab === 'tecnica') {
            return (
                <SensitiveDataGuard userId={props.userId} userName={displayName(props.userId)} storageKey="stats_tecnica">
                    <StatsTecnicaTab
                        castellersInfo={castellersInfo}
                        setCastellersInfo={setCastellersInfo}
                        {...props}
                    />
                </SensitiveDataGuard>
            );
        }

        if (activeTab === 'assaigs') {
            if (!can(userInfo, 'VIEW_ASSAIGS_STATS')) return null;
            return (
                <StatsAssaigsTab
                    events={events}
                    castellersInfo={castellersInfo}
                    filtersEvents={filtersEvents}
                />
            );
        }

        if (activeTab === 'proves') {
            if (!can(userInfo, 'VIEW_PROVES_STATS')) return null;
            return (
                <StatsProvesTab
                    events={events}
                />
            );
        }

        if (activeTab === 'mapa') {
            if (!can(userInfo, 'VIEW_CONNECTIONS_MAP')) return null;
            return (
                <StatsMapaTab
                    castellersInfo={castellersInfo}
                />
            );
        }

        return null;
    };

    const all_users = Object.keys(castellersInfo)
        .filter(user => !filtersEvents.canalla || castellersInfo[user].canalla !== 1)
        .filter(user => !filtersEvents.music || castellersInfo[user].music !== 1)
        .map(user => ({
            value: user,
            label: displayName(user)
        }));

    return (
        <div
            style={{ width: '90%', boxSizing: 'border-box', paddingTop: 50, paddingBottom: 50 }}
        >
            <UserInfo castellersInfo={castellersInfo} {...props} />

            <HeaderTabs {...props} />

            <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                ← Tornar a la pàgina principal
            </Pressable>

            <h1>Estadístiques</h1>

            <div className="page-layout">
                <div className="navigation-tabs">
                    <button
                        className={`tab-button ${activeTab === 'assistencia-global' ? 'active' : ''}`}
                        onClick={() => changeTab('assistencia-global')}
                    >
                        <i className="fas fa-chart-bar"></i> Assistències
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'individuals' ? 'active' : ''}`}
                        onClick={() => changeTab('individuals')}
                    >
                        <i className="fas fa-users"></i> Individuals
                    </button>
                    {can(userInfo, 'VIEW_ASSAIGS_STATS') ? (
                        <button
                            className={`tab-button ${activeTab === 'assaigs' ? 'active' : ''}`}
                            onClick={() => changeTab('assaigs')}
                        >
                            <i className="fas fa-calendar-alt"></i> Assaigs
                        </button>
                    ) : null}
                    {can(userInfo, 'VIEW_PROVES_STATS') ? (
                        <button
                            className={`tab-button ${activeTab === 'proves' ? 'active' : ''}`}
                            onClick={() => changeTab('proves')}
                        >
                            <i className="fas fa-clipboard-list"></i> Proves
                        </button>
                    ) : null}
                    {can(userInfo, 'VIEW_POSITIONS_STATS') ? (
                        <button
                            className={`tab-button ${activeTab === 'posicions' ? 'active' : ''}`}
                            onClick={() => changeTab('posicions')}
                        >
                            <i className="fas fa-map-marker-alt"></i> Posicions
                        </button>
                    ) : null}
                    {can(userInfo, 'VIEW_TECH_STATS') ? (
                        <button
                            className={`tab-button ${activeTab === 'tecnica' ? 'active' : ''}`}
                            onClick={() => changeTab('tecnica')}
                        >
                            <i className="fas fa-users-cog"></i> Tècnica
                        </button>
                    ) : null}
                    {can(userInfo, 'VIEW_CONNECTIONS_MAP') ? (
                        <button
                            className={`tab-button ${activeTab === 'mapa' ? 'active' : ''}`}
                            onClick={() => changeTab('mapa')}
                        >
                            <i className="fas fa-globe"></i> Mapa
                        </button>
                    ) : null}
                    <button
                        className={`tab-button ${activeTab === 'exportacio' ? 'active' : ''}`}
                        onClick={() => changeTab('exportacio')}
                    >
                        <i className="fas fa-file-export"></i> Exportació
                    </button>
                </div>

                <div className="page-content">
                    {renderTabContent()}
                </div>
            </div>

            <div style={{ height: 100 }}>&nbsp;</div>
        </div>
    )
}

export default StatsApp;
